import { useCallback, useEffect, useRef, useState } from 'react';

// Only warehouse preferences survive a page mount. All other filters stay in React state.
function warehouseStorageKey(key) {
  return key.replace(/Filters$/, 'WarehouseId');
}

function readFilters(key, defaults) {
  const next = { ...defaults };
  if (!Object.prototype.hasOwnProperty.call(defaults, 'warehouse_id')) return next;
  // Undefined distinguishes a missing preference from an explicit All Warehouses choice.
  next.warehouse_id = undefined;
  try {
    const warehouse = localStorage.getItem(warehouseStorageKey(key));
    if (warehouse !== null) next.warehouse_id = warehouse;
    else {
      // Migrate only the warehouse from the former page filter object.
      const legacy = JSON.parse(localStorage.getItem(key));
      if (typeof legacy?.warehouse_id === 'string' || typeof legacy?.warehouse_id === 'number') {
        next.warehouse_id = String(legacy.warehouse_id);
      }
    }
  } catch { /* Optional preference. */ }
  return next;
}

function save(key, value) {
  try {
    if (localStorage.getItem(key) !== value) localStorage.setItem(key, value);
  } catch { /* Filtering still works when browser storage is unavailable. */ }
}

export function validateFilters(filters, defaults, options = {}, firstWarehouse = false, preserveEmptyWarehouse = false) {
  const next = { ...filters };
  Object.entries(options).forEach(([name, values]) => {
    const allowed = values.map(String);
    if (name === 'warehouse_id' && firstWarehouse) {
      next[name] = preserveEmptyWarehouse && String(next[name]) === 'all'
        ? 'all'
        : allowed.includes(String(next[name])) ? String(next[name]) : allowed.find(Boolean) || '';
    } else if (next[name] && !allowed.includes(String(next[name]))) {
      next[name] = defaults[name];
    }
  });
  return next;
}

export default function usePersistentFilters(key, defaults, { searchParams, setSearchParams, urlKeys = {}, navigationFilters, restoreWarehouseEarly = false } = {}) {
  const config = useRef();
  const warehouseUrlKeys = Object.fromEntries(Object.entries(urlKeys).filter(([name]) => name === 'warehouse_id'));
  config.current = {
    key, defaults, searchParams, setSearchParams, urlKeys: warehouseUrlKeys,
    transientUrlKeys: Object.entries(urlKeys).filter(([name]) => name !== 'warehouse_id').map(([, parameter]) => parameter),
  };
  function fromUrl(base) {
    const next = { ...base };
    Object.entries(warehouseUrlKeys).forEach(([name, parameter]) => {
      if (searchParams?.has(parameter)) {
        let value = searchParams.get(parameter) || '';
        if (name === 'warehouse_id' && value === 'all') value = '';
        next[name] = value;
      }
    });
    return next;
  }
  const [filters, setState] = useState(() => {
    if (!restoreWarehouseEarly) return { ...fromUrl(defaults), ...navigationFilters };
    const saved = readFilters(key, defaults);
    const initial = { ...defaults, ...saved };
    if (initial.warehouse_id === undefined) initial.warehouse_id = defaults.warehouse_id;
    return { ...fromUrl(initial), ...navigationFilters };
  });
  const current = useRef(filters);
  const pending = useRef(null);
  if (pending.current === null) {
    const saved = readFilters(key, defaults);
    pending.current = { ...fromUrl(saved), ...navigationFilters };
  }
  const initialized = useRef(false);
  const [ready, setReady] = useState(false);
  const lastUrl = useRef(searchParams?.toString());
  const lastNavigationFilters = useRef(navigationFilters);

  const commit = useCallback((next, syncUrl = true) => {
    const cfg = config.current;
    next = Object.fromEntries(Object.entries(cfg.defaults).map(([name, fallback]) => [
      name, typeof next[name] === 'string' || typeof next[name] === 'number' ? String(next[name]) : fallback,
    ]));
    if (Object.prototype.hasOwnProperty.call(cfg.defaults, 'warehouse_id')) {
      save(warehouseStorageKey(cfg.key), next.warehouse_id);
    }
    try { localStorage.removeItem(cfg.key); } catch { /* Optional legacy preference cleanup. */ }
    const changed = JSON.stringify(current.current) !== JSON.stringify(next);
    current.current = next;
    if (changed) setState(next);
    if (syncUrl && cfg.setSearchParams) {
      const params = new URLSearchParams(cfg.searchParams);
      cfg.transientUrlKeys.forEach(parameter => params.delete(parameter));
      Object.entries(cfg.urlKeys).forEach(([name, parameter]) => {
        if (next[name]) params.set(parameter, next[name]); else params.delete(parameter);
      });
      if (params.toString() !== cfg.searchParams.toString()) {
        lastUrl.current = params.toString();
        cfg.setSearchParams(params, { replace: !initialized.current });
      }
    }
    return changed;
  }, []);

  const setFilters = useCallback(update => {
    const base = initialized.current ? current.current : pending.current;
    const next = typeof update === 'function' ? update(base) : update;
    commit(next);
    initialized.current = true;
    setReady(true);
  }, [commit]);

  const reconcile = useCallback(validate => {
    const next = validate(initialized.current ? current.current : pending.current);
    const changed = commit(next);
    initialized.current = true;
    setReady(true);
    return changed;
  }, [commit]);

  useEffect(() => {
    const cfg = config.current;
    const url = cfg.searchParams?.toString();
    // A new navigation payload is applied once; ordinary filter/URL updates
    // must not restore the original Dashboard selection over manual changes.
    const navigationChanged = navigationFilters && navigationFilters !== lastNavigationFilters.current;
    lastNavigationFilters.current = navigationFilters;
    if (url === lastUrl.current && !navigationChanged) return;
    lastUrl.current = url;
    const next = { ...current.current };
    Object.entries(cfg.urlKeys).forEach(([name, parameter]) => {
      let value = cfg.searchParams.get(parameter) || '';
      if (name === 'warehouse_id' && value === 'all') value = '';
      next[name] = value;
    });
    if (navigationChanged) Object.assign(next, navigationFilters);
    pending.current = next;
    initialized.current = false;
    current.current = next;
    setState(next);
    setReady(false);
  }, [searchParams, navigationFilters]);

  return [filters, setFilters, reconcile, ready];
}
